import React, { useEffect, useState, useRef } from "react";
import { fireStore } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const SalesCoaching = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 1);

  const [startDate, setStartDate] = useState(threeDaysAgo.getTime()); // Store as milliseconds
  const [endDate, setEndDate] = useState(Date.now()); // Store as milliseconds

  const [SSCoachings, setSSCoachings] = useState([]);
  const [filteredSSCoachings, setFilteredSSCoachings] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");

  // Fetch Sales Coachings...
  useEffect(() => {
    const fetchSSCoachings = async () => {
      try {
        const collectionRef = collection(fireStore, "SSCoachings");
        const q = query(
          collectionRef,
          where("id", ">=", startDate),
          where("id", "<=", endDate)
        );
        const querySnapshot = await getDocs(q);

        const SSCoachingsList = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,

            ...doc.data(),
          };
        });

        const sortedSalesList = SSCoachingsList.sort((a, b) => b.id - a.id);
        setSSCoachings(sortedSalesList);
        setFilteredSSCoachings(sortedSalesList);
        // console.log(SSCoachingsList);
      } catch (error) {
        console.error("Error fetching SSCoachings:", error);
      }
    };

    fetchSSCoachings();
  }, [startDate, endDate]);

  // Fetch Territories...
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };

    fetchTerritories();
  }, []);

  // Export PDF...
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 0;

    for (let i = 0; i < filteredSSCoachings.length; i++) {
      const SaleCoachingElement = document.getElementById(
        `SaleCoaching-${filteredSSCoachings[i].id}`
      );

      if (SaleCoachingElement) {
        const canvas = await html2canvas(SaleCoachingElement, { scale: 1 });
        const imgData = canvas.toDataURL("image/jpeg", 0.7);
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (yPosition + imgHeight > pageHeight) {
          doc.addPage();
          yPosition = 0;
        }

        doc.addImage(imgData, "PNG", 0, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight;
      }
    }

    doc.save("SaleCoachings.pdf");
  };

  // Territory Filters
  useEffect(() => {
    const filtered = SSCoachings.filter(
      (visit) =>
        visit.user?.territory.name.toLowerCase() ===
        selectedTerritory.toLocaleLowerCase()
    );

    setFilteredSSCoachings(filtered);
    if (selectedTerritory === "All") {
      setFilteredSSCoachings(SSCoachings);
    }
  }, [selectedTerritory]);

  //Search Filter...
  useEffect(() => {
    const filtered = SSCoachings.filter((visit) =>
      visit.user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredSSCoachings(filtered);
    if (searchQuery === "All") {
      setFilteredSSCoachings(SSCoachings);
    }
  }, [searchQuery]);

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      <div className="bg-white px-4 py-2 rounded-lg shadow-lg max-w-5xl mx-auto mt-2 lg:sticky top-0 z-50">
        <div className="flex flex-wrap justify-between items-center py-2">
          <h1 className="text-lg font-semibold text-liquidblue">
            Sales Coaching
          </h1>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-3 py-1"
          >
            PDF
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            onChange={(e) => setSelectedTerritory(e.target.value)}
            value={selectedTerritory}
          >
            <option value="All">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          <div className="flex items-center">
            {/* From Date Picker */}
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(startDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setStartDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center ">
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              id="from-date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(endDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setEndDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>
      </div>
      {filteredSSCoachings.map((item) => (
        <div
          id={`SaleCoaching-${item.id}`}
          key={item.id}
          className="bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto mt-4"
        >
          {/*<h1 className="text-xl font-semibold py-4 text-liquidblue">{item.user?.territory?.name}</h1>*/}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="flex justify-around">
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.name}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.phone}
                  </th>

                  <th className="py-2 px-4 text-left">
                    {new Date(Number(item.id)).toLocaleDateString("en-GB")}{" "}
                    {new Date(Number(item.id)).toLocaleTimeString("en-GB", {
                      hour12: true,
                    })}{" "}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    <th className="py-2 px-4 border-b border-gray-200 text-left">
                      {item.visitedTerritory ? (
                        <h1 className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-4 py-1">
                          {item.visitedTerritory || "N/A"}
                        </h1>
                      ) : (
                        "N/A"
                      )}
                    </th>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="flex flex-row w-full">
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-8">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">customerName</span>
                        <span className=" font-lato font-bold">
                          {item.customerName}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">farmer</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.farmer}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">objectivesPlanned</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.objectivePlanned}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">introduction</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.introduction}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">openEndQuests</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.openEndQuests}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">gettingAttention</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.gettingAttention}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">openEndQuests</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.openEndQuests_2}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-8">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">featureVsBenefit</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.featureVsBenefit}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">benefitsTagQuests</span>
                        <div class="dark:bg-white">
                          <label className="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.benefitsTagQuests}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">responseHandle</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.responseHandle}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">catchBuySignals</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.catchBuySigs}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">closing</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.closing}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">commitment</span>
                        <label class="text-white">
                          <input
                            className="dark:border-white-400/20 w-5 h-5"
                            type="checkbox"
                            checked={item.commitment}
                          />
                        </label>
                      </div>

                      <div className="flex items-center ">
                        <span className="font-lato font-bold">feedBack</span>
                      </div>
                      <div className="flex items-center">
                        <p className="w-full break-words font-lato font-extrabold">
                          {item.feedBack}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SalesCoaching;
